// rendszergazda fül

import { useDoubleConfirm } from '../contexts/AlertContext';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { useAlert, useConfirm } from '../contexts/AlertContext';
import PasswordInput from '../components/PasswordInput';


export default function SuperAdminPanel() {
    // A felület állapotai: aktív fül, betöltött adatok, szűrők és törlés.
    const { user } = useAuth();
    const showAlert = useAlert();
    const confirm = useConfirm();
        const doubleConfirm = useDoubleConfirm();
    const [activeTab, setActiveTab] = useState('users'); 
    const [users, setUsers] = useState([]);
    const [shopInfo, setShopInfo] = useState({
        address: '', phone1: '', phone2: '', email1: '', email2: ''
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('Összes szerepkör');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteDeleting, setDeleteDeleting] = useState(false);

    // Adminisztrátori adatokat lekérése (rendszergazda jogosultsággal).
    useEffect(() => {
        if (user && user.role === 'Rendszergazda') {
            fetchUsers();
            fetchShopInfo();
        }
    }, [user]);

    // Bejelentkezés nélkül a bejelentkezési oldalra, jogosultság nélkül a profilra irányítunk.
    if (!user) return <Navigate to="/bejelentkezes" replace />;
    if (user.role !== 'Rendszergazda') return <Navigate to="/profil" replace />;

    // Felhasználók lekérése, és a kérés végén lezárja a kezdeti betöltési állapotot.
    const fetchUsers = async () => {
        try {
            const usersRes = await axios.get(`/api/admin/users`);
            setUsers(usersRes.data);
        } catch (err) {
            console.error("Hiba a felhasználók betöltésekor:", err);
        } finally {
            setLoading(false);
        }
    };

    // Üzlet jelenlegi elérhetőségi adatainak lekérése.
    const fetchShopInfo = async () => {
        try {
            const shopInfoRes = await axios.get(`/api/shop-info`);
            if (shopInfoRes.data) setShopInfo(shopInfoRes.data);
        } catch (err) {
            console.error("Hiba az üzletadatok lekérésekor:", err);
        }
    };

    // Menti a kiválasztott felhasználó új szerepkörét, majd frissíti a listát.
    const handleRoleChange = async (id, newRole) => {
        try {
            await axios.put(`/api/admin/users/${id}/role`, { role: newRole });
            fetchUsers();
        } catch (err) {
            showAlert(err.response?.data?.error || "Hiba a szerepkör mentésekor!");
        }
    };

    // Fiók törlés ha nem rendszergazda.
    const handleDelete = async (u) => {
        if (u.role === 'Rendszergazda') return showAlert("Rendszergazdát nem törölhetsz!");
        if (!await doubleConfirm(`Biztosan törlöd ${u.name} felhasználót?`, "Ez végleges művelet: a felhasználó fiókja és minden kapcsolódó foglalása törlődik. Folytatod?")) return;

        setDeleteTarget(u);
    };

    // A törléshez bekéri rendszergazda jelszavát.
    const confirmDelete = async (e) => {
        e.preventDefault();
        setDeleteDeleting(true);
        try {
            await axios.delete(`/api/admin/users/${deleteTarget.id}`, {
                data: { adminId: user.id, password: deletePassword }
            });
            fetchUsers();
        } catch (err) {
            showAlert("Hiba a felhasználó törlésekor: " + (err.response?.data?.error || "Sikertelen művelet"));
        } finally {
            setDeleteDeleting(false);
            setDeleteTarget(null);
            setDeletePassword('');
        }
    };

    // Üzlet adatainak módosítása, és rövid visszajelzés a mentésről.
    const handleShopSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/api/admin/shop-info`, shopInfo);
            setMessage("✅ Adatok sikeresen mentve!");
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            showAlert("Hiba a bolt adatai mentésekor!");
        }
    };

    // Lista szűrése.
    const filteredUsers = users.filter((currentUser) => {
        const search = userSearch.trim().toLowerCase();
        const matchesSearch = !search || currentUser.name.toLowerCase().includes(search) || currentUser.email.toLowerCase().includes(search);
        const matchesRole = roleFilter === 'Összes szerepkör' || currentUser.role === roleFilter;
        return matchesSearch && matchesRole;
    });

    // A kezelőfelület csak a kezdeti felhasználó-lekérés befejezése után jelenik meg.
    if (loading) return <div className="p-20 text-center font-black animate-pulse dark:text-white dark:bg-gray-900 min-h-screen">BETÖLTÉS...</div>;

    return (
        <div className="min-h-screen bg-transparent transition-colors duration-300 py-6 sm:py-10 px-2 sm:px-4">
            <div className="max-w-6xl mx-auto p-4 sm:p-8 bg-transparent shadow-none rounded-2xl sm:rounded-[2.5rem] mt-4 sm:mt-10 border border-transparent transition-colors">
                
                {/* A felhasználókezelés és az üzletadatok közötti navigáció. */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10 border-b border-gray-100 dark:border-gray-700 pb-4">
                    <button 
                        onClick={() => setActiveTab('users')} 
                        className={`w-full sm:w-auto px-6 sm:px-8 py-3 rounded-[0.5rem] sm:rounded-[0.5rem] font-black uppercase text-xs transition-all ${
                            activeTab === 'users' 
                                ? 'bg-blue-600 dark:bg-blue-700 text-white shadow-lg' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        👥 Felhasználók kezelése
                    </button>
                    <button 
                        onClick={() => setActiveTab('shop')} 
                        className={`w-full sm:w-auto px-6 sm:px-8 py-3 rounded-[0.5rem] sm:rounded-[0.5rem] font-black uppercase text-xs transition-all ${
                            activeTab === 'shop' 
                                ? 'bg-purple-600 dark:bg-purple-700 text-white shadow-lg' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        🏠 Üzlet adatai
                    </button>
                </div>

                {activeTab === 'users' ? (
                    <div>
                        <h3 className="text-xl sm:text-2xl font-black mb-6 uppercase tracking-tighter dark:text-white">
                            Felhasználók <span className="text-blue-600 dark:text-blue-400">névsora</span>
                        </h3>

                        <div className="flex flex-col sm:flex-row gap-3 mb-6">
                            <label className="flex-1">
                                <span className="sr-only">Felhasználó keresése</span>
                                <input
                                    type="search"
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    placeholder="🔎 Név vagy email keresése"
                                    className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-100 dark:border-gray-600 rounded-xl sm:rounded-xl focus:border-blue-500 dark:focus:border-blue-400 outline-none text-sm font-bold dark:text-white dark:placeholder-gray-400"
                                />
                            </label>
                            <label className="sm:w-56">
                                <span className="sr-only">Szerepkör szűrése</span>
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-100 dark:border-gray-600 rounded-xl sm:rounded-2xl focus:border-blue-500 dark:focus:border-blue-400 outline-none text-sm font-bold dark:text-white"
                                >
                                    <option>Összes szerepkör</option>
                                    <option>Vásárló</option>
                                    <option>Fodrász</option>
                                    <option>Rendszergazda</option>
                                </select>
                            </label>
                        </div>

                        {/* Mobilnézet: a felhasználók külön kártyákon jelennek meg. */}
                        <div className="block sm:hidden space-y-3">
                            {filteredUsers.map(u => (
                                <div key={u.id} className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 space-y-4">
                                    <div>
                                        <div className="font-black text-gray-800 dark:text-white uppercase text-sm">{u.name}</div>
                                        <div className="text-xs text-gray-400 dark:text-gray-500 break-all">{u.email}</div>
                                    </div>
                                    <div className="flex flex-col gap-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                        <select 
                                            value={u.role} 
                                            disabled={u.role === 'Rendszergazda'} 
                                            onChange={(e) => handleRoleChange(u.id, e.target.value)} 
                                            className="w-full text-xs font-black uppercase p-3 rounded-xl bg-white dark:bg-gray-800 dark:text-white border border-gray-200 dark:border-gray-600 shadow-sm cursor-pointer"
                                        >
                                            <option value="Vásárló">Vásárló</option>
                                            <option value="Fodrász">Fodrász</option>
                                            {u.role === 'Rendszergazda' && <option value="Rendszergazda">Rendszergazda</option>}
                                        </select>
                                        {u.role === 'Vásárló' && (
                                            <button onClick={() => handleDelete(u)} className="w-full text-red-500 hover:text-white hover:bg-red-600 border border-red-200 dark:border-red-900/50 p-3 rounded-xl text-xs font-black uppercase transition-colors">🗑️ Törlés</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {filteredUsers.length === 0 && <p className="py-8 text-center text-sm text-gray-400">Nincs találat.</p>}
                        </div>

                        {/* Nagyobb képernyőn a felhasználók táblázatban jelennek meg. */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full text-left border-separate border-spacing-y-2">
                                <thead>
                                    <tr className="text-[10px] uppercase font-black text-gray-400 dark:text-gray-500 italic">
                                        <th className="px-6 py-2">Név / Email</th>
                                        <th className="px-6 py-2">Jogosultság</th>
                                        <th className="px-6 py-2 text-right">Művelet</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(u => (
                                        <tr key={u.id} className="bg-gray-50/50 dark:bg-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all rounded-[0.5rem]">
                                            <td className="px-6 py-4 rounded-l-3xl border-y-[3px] border-l-[5px] border-purple-200 dark:border-gray-600 ">
                                                <div className="font-black text-gray-800 dark:text-white uppercase text-sm">{u.name}</div>
                                                <div className="text-[10px] text-gray-400 dark:text-gray-500">{u.email}</div>
                                            </td>
                                            <td className="px-6 py-4 border-y-[3px] border-gray-150 dark:border-gray-600">
                                                <select value={u.role} disabled={u.role === 'Rendszergazda'} onChange={(e) => handleRoleChange(u.id, e.target.value)} className="text-[10px] font-black uppercase p-2 rounded-xl bg-white border-none shadow-sm cursor-pointer">
                                                    <option value="Vásárló">Vásárló</option>
                                                    <option value="Fodrász">Fodrász</option>
                                                    {u.role === 'Rendszergazda' && <option value="Rendszergazda">Rendszergazda</option>}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 rounded-r-2xl border-y-[3px] border-r-[5px] border-purple-200 dark:border-gray-600 text-right">
                                                {u.role === 'Vásárló' && <button onClick={() => handleDelete(u)} className="text-red-400 hover:text-red-600 p-2">🗑️</button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Az üzlet adatainak szerkesztőűrlapja. */}
                        <h3 className="text-xl sm:text-2xl font-black mb-2 uppercase tracking-tighter dark:text-white">
                            Üzlet <span className="text-purple-600 dark:text-purple-400">beállításai</span>
                        </h3>
                        <p className="text-gray-400 dark:text-gray-500 text-[10px] mb-6 sm:mb-8 font-bold uppercase">A Contact oldalon megjelenő adatok módosítása</p>
                        {message && <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-2xl font-bold border-2 border-green-200 dark:border-green-700 transition-colors">{message}</div>}
                        <form onSubmit={handleShopSubmit} className="space-y-4 sm:space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div className="sm:col-span-2">
                                    <label className="block text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 mb-1 sm:mb-2 ml-2">Cím</label>
                                    <input type="text" value={shopInfo.address} onChange={(e) => setShopInfo({...shopInfo, address: e.target.value})} className="w-full p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 border-2 border-white dark:border-gray-600 rounded-xl sm:rounded-2xl focus:border-purple-600 dark:focus:border-purple-400 transition-all font-bold outline-none shadow-inner dark:text-white dark:placeholder-gray-400 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 mb-1 sm:mb-2 ml-2">Telefon 1</label>
                                    <input type="text" value={shopInfo.phone1} onChange={(e) => setShopInfo({...shopInfo, phone1: e.target.value})} className="w-full p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 border-2 border-white dark:border-gray-600 rounded-xl sm:rounded-2xl focus:border-purple-600 dark:focus:border-purple-400 transition-all font-bold outline-none shadow-inner dark:text-white dark:placeholder-gray-400 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 mb-1 sm:mb-2 ml-2">Telefon 2 (Opcionális)</label>
                                    <input type="text" value={shopInfo.phone2} onChange={(e) => setShopInfo({...shopInfo, phone2: e.target.value})} className="w-full p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 border-2 border-white dark:border-gray-600 rounded-xl sm:rounded-2xl focus:border-purple-600 dark:focus:border-purple-400 transition-all font-bold outline-none shadow-inner dark:text-white dark:placeholder-gray-400 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 mb-1 sm:mb-2 ml-2">Email 1</label>
                                    <input type="email" value={shopInfo.email1} onChange={(e) => setShopInfo({...shopInfo, email1: e.target.value})} className="w-full p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 border-2 border-white dark:border-gray-600 rounded-xl sm:rounded-2xl focus:border-purple-600 dark:focus:border-purple-400 transition-all font-bold outline-none shadow-inner dark:text-white dark:placeholder-gray-400 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 mb-1 sm:mb-2 ml-2">Email 2 (Opcionális)</label>
                                    <input type="email" value={shopInfo.email2} onChange={(e) => setShopInfo({...shopInfo, email2: e.target.value})} className="w-full p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 border-2 border-white dark:border-gray-600 rounded-xl sm:rounded-2xl focus:border-purple-600 dark:focus:border-purple-400 transition-all font-bold outline-none shadow-inner dark:text-white dark:placeholder-gray-400 text-sm" />
                                </div>
                            </div>
                            <button type="submit" className="w-full sm:w-auto bg-purple-600 dark:bg-purple-700 hover:bg-black dark:hover:bg-purple-800 text-white font-black uppercase px-8 sm:px-12 py-4 rounded-xl sm:rounded-2xl shadow-xl transition-all transform active:scale-95 text-xs sm:text-sm">💾 Adatok mentése</button>
                        </form>
                    </div>
                )}
            </div>

            {/* A végleges törlés csak a rendszergazda jelszavának megadásával hajtható végre. */}
            {deleteTarget && (
                <div className="fixed inset-0 z-[210] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm">
                    <form
                        onSubmit={confirmDelete}
                        className="w-full max-w-md rounded-[2rem] border border-red-200 bg-white p-7 shadow-2xl dark:border-red-900 dark:bg-gray-800"
                    >
                        <h2 className="mb-3 text-xl font-black text-gray-900 dark:text-white">Jelszó szükséges</h2>
                        <p className="mb-6 text-sm font-semibold leading-6 text-gray-600 dark:text-gray-300">
                            A(z) {deleteTarget.name} fiók törlésének megerősítéséhez írd be a saját jelszavad.
                        </p>
                        <label htmlFor="delete-account-password" className="mb-2 block text-sm font-black text-gray-700 dark:text-gray-200">
                            Jelenlegi jelszó
                        </label>
                        <PasswordInput
                            id="delete-account-password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            placeholder="Írd be a saját jelszavad"
                            className="w-full rounded-xl border-2 border-blue-300 p-3 text-base dark:border-blue-500 dark:bg-gray-700 dark:text-white"
                            required
                            autoFocus
                        />
                        <div className="mt-7 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setDeleteTarget(null);
                                    setDeletePassword('');
                                }}
                                className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-black text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                                Mégse
                            </button>
                            <button
                                type="submit"
                                disabled={deleteDeleting}
                                className="rounded-xl bg-red-600 px-5 py-3 text-sm font-black text-white shadow-lg transition-colors hover:bg-red-700 disabled:opacity-50"
                            >
                                {deleteDeleting ? 'Törlés...' : 'Fiók törlése'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}